import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiBookOpen, FiClipboard, FiFileText, FiUsers, FiUserCheck } from 'react-icons/fi';
import styles from './AdminDashboardPage.module.css';
import { AdminService } from '../services/admin.service';
import { UsersService } from '../services/users.service';
import { DeveloperRequestsService } from '../services/developer-requests.service';
import { TransferProofService } from '../services/transfer-proof.service';
import { BlogService } from '../services/blog.service';
import { AdminDevelopmentsService } from '../services/adminDevelopments.service';

type ManagerDashboardData = {
  stats: Awaited<ReturnType<typeof AdminService.getStats>> | null;
  pendingUsers: Awaited<ReturnType<typeof UsersService.getPending>>;
  pendingDeveloperRequests: Awaited<ReturnType<typeof DeveloperRequestsService.getAll>> | null;
  pendingTransferProofsTotal: number;
  recentBlogs: Awaited<ReturnType<typeof BlogService.getAdminPosts>>;
  blogCategories: Awaited<ReturnType<typeof BlogService.getAdminCategories>>;
  developments: Awaited<ReturnType<typeof AdminDevelopmentsService.list>> | null;
};

export default function ManagerDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ManagerDashboardData | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    Promise.allSettled([
      AdminService.getStats(),
      UsersService.getPending(),
      DeveloperRequestsService.getAll({ status: 'PENDING' }),
      TransferProofService.getAll({ status: 'PENDING' }),
      BlogService.getAdminPosts(),
      BlogService.getAdminCategories(),
      AdminDevelopmentsService.list({ page: 1, pageSize: 50 }),
    ]).then((results) => {
      if (!alive) return;

      const [
        statsR,
        usersR,
        devReqR,
        proofsR,
        blogsR,
        catsR,
        developmentsR,
      ] = results;

      setData({
        stats: statsR.status === 'fulfilled' ? statsR.value : null,
        pendingUsers: usersR.status === 'fulfilled' && Array.isArray(usersR.value) ? usersR.value : [],
        pendingDeveloperRequests:
          devReqR.status === 'fulfilled' ? devReqR.value : null,
        pendingTransferProofsTotal:
          proofsR.status === 'fulfilled' ? Number(proofsR.value?.total || 0) : 0,
        recentBlogs: blogsR.status === 'fulfilled' && Array.isArray(blogsR.value) ? blogsR.value : [],
        blogCategories: catsR.status === 'fulfilled' && Array.isArray(catsR.value) ? catsR.value : [],
        developments: developmentsR.status === 'fulfilled' ? developmentsR.value : null,
      });
      setLoading(false);
    });

    return () => {
      alive = false;
    };
  }, []);

  const docPipeline = useMemo(() => {
    const items =
      data?.developments && (data.developments as any).ok !== false
        ? ((data.developments as any).data?.items ?? [])
        : [];
    const counts = {
      inReview: 0,
      observed: 0,
      uploaded: 0,
      approved: 0,
    };
    for (const d of items) {
      const s = String(d.documentation_status || 'NONE');
      if (s === 'IN_REVIEW') counts.inReview += 1;
      else if (s === 'OBSERVED') counts.observed += 1;
      else if (s === 'UPLOADED') counts.uploaded += 1;
      else if (s === 'APPROVED') counts.approved += 1;
    }
    return counts;
  }, [data?.developments]);

  const topBlogs = useMemo(() => {
    const items = [...(data?.recentBlogs || [])];
    items.sort((a: any, b: any) => {
      const ad = new Date(b.updated_at || b.created_at || 0).getTime();
      const bd = new Date(a.updated_at || a.created_at || 0).getTime();
      return ad - bd;
    });
    return items.slice(0, 4);
  }, [data?.recentBlogs]);

  if (loading) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>Panel de manager</h1>
        </header>
        <div className={styles.statsGrid}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className={`${styles.statCard} ${styles.skeletonCard}`} />
          ))}
        </div>
      </div>
    );
  }

  const pendingUsersCount = data?.pendingUsers?.length || 0;
  const pendingDevReqCount = Number(data?.pendingDeveloperRequests?.total || 0);
  const totalBlogs = Number(data?.recentBlogs?.length || 0);
  const totalCategories = Number(data?.blogCategories?.length || 0);

  const kpis = [
    { label: 'Proyectos activos', value: String(data?.stats?.activeProjects || 0), icon: FiClipboard },
    { label: 'Usuarios totales', value: String(data?.stats?.totalUsers || 0), icon: FiUsers },
    { label: 'Usuarios pendientes', value: String(pendingUsersCount), icon: FiUserCheck },
    { label: 'Comprobantes pendientes', value: String(data?.pendingTransferProofsTotal || 0), icon: FiFileText },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Panel de manager</h1>
      </header>

      <section className={styles.statsGrid}>
        {kpis.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <article key={`${kpi.label}-${idx}`} className={styles.statCard}>
              <div className={styles.statHeader}>
                <div className={styles.labelWrap}>
                  <span className={styles.statLabel}>{kpi.label}</span>
                </div>
                <div className={styles.iconBox}>
                  <Icon />
                </div>
              </div>
              <div className={styles.statMain}>
                <div className={styles.statValue}>{kpi.value}</div>
              </div>
            </article>
          );
        })}
      </section>

      <section className={styles.sectionGrid}>
        <article className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <h2 className={styles.sectionCardTitle}>Bandeja de trabajo</h2>
          </div>
          <div className={styles.queueList}>
            <Link to="/admin/aprobacion-usuarios" className={styles.queueItem}>
              <span>Usuarios pendientes</span>
              <strong>{pendingUsersCount}</strong>
            </Link>
            <Link to="/admin/solicitudes-desarrollador" className={styles.queueItem}>
              <span>Solicitudes de desarrollador</span>
              <strong>{pendingDevReqCount}</strong>
            </Link>
            <Link to="/admin/comprobantes-transferencia" className={styles.queueItem}>
              <span>Comprobantes por revisar</span>
              <strong>{data?.pendingTransferProofsTotal || 0}</strong>
            </Link>
            <Link to="/admin/proyectos/documentos" className={styles.queueItem}>
              <span>Proyectos con documentación pendiente</span>
              <strong>{docPipeline.inReview + docPipeline.observed + docPipeline.uploaded}</strong>
            </Link>
          </div>
        </article>

        <article className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <h2 className={styles.sectionCardTitle}>Documentación de proyectos</h2>
          </div>
          <div className={styles.statusRows}>
            <div className={styles.statusRow}><span>En revisión</span><strong>{docPipeline.inReview}</strong></div>
            <div className={styles.statusRow}><span>Observados</span><strong>{docPipeline.observed}</strong></div>
            <div className={styles.statusRow}><span>Subidos</span><strong>{docPipeline.uploaded}</strong></div>
            <div className={styles.statusRow}><span>Aprobados</span><strong>{docPipeline.approved}</strong></div>
          </div>
          <Link to="/admin/proyectos/documentos" className={styles.textLink}>Ir a revisión documental</Link>
        </article>
      </section>

      <section className={styles.sectionGrid}>
        <article className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <h2 className={styles.sectionCardTitle}>Contenido / Blogs</h2>
          </div>
          <div className={styles.inlineStats}>
            <div className={styles.inlineStat}><FiBookOpen /> <span>Blogs</span><strong>{totalBlogs}</strong></div>
            <div className={styles.inlineStat}><FiFileText /> <span>Categorías</span><strong>{totalCategories}</strong></div>
          </div>
          <div className={styles.miniList}>
            {topBlogs.length === 0 ? (
              <div className={styles.emptyLine}>Sin blogs recientes</div>
            ) : topBlogs.map((blog: any) => (
              <div key={String(blog.id)} className={styles.miniListItem}>
                <div className={styles.miniListTitle}>{blog.title}</div>
                <div className={styles.miniListMeta}>
                  {blog.updated_at || blog.created_at || '—'}
                  {' • '}
                  Vistas: {blog.views_count ?? blog.views ?? 'N/D'}
                </div>
              </div>
            ))}
          </div>
          <div className={styles.actionRow}>
            <Link to="/admin/blogs" className={styles.actionPill}>Gestionar blogs</Link>
            <Link to="/admin/blogs/categorias" className={styles.actionPill}>Categorías</Link>
          </div>
        </article>

        <article className={styles.sectionCard}>
          <div className={styles.sectionCardHeader}>
            <h2 className={styles.sectionCardTitle}>Actividad reciente</h2>
          </div>
          <div className={styles.miniList}>
            {data?.pendingUsers?.slice(0, 3).map((u) => (
              <div key={u.id} className={styles.miniListItem}>
                <div className={styles.miniListTitle}>{u.name}</div>
                <div className={styles.miniListMeta}>{u.email} • {u.role}</div>
              </div>
            ))}
            {(data?.pendingDeveloperRequests?.data || []).slice(0, 2).map((r) => (
              <div key={r.id} className={styles.miniListItem}>
                <div className={styles.miniListTitle}>{r.user?.name || `Solicitud ${r.id}`}</div>
                <div className={styles.miniListMeta}>Developer request • {r.status}</div>
              </div>
            ))}
            {!pendingUsersCount && !pendingDevReqCount ? (
              <div className={styles.emptyLine}>Sin actividad pendiente relevante</div>
            ) : null}
          </div>
        </article>
      </section>
    </div>
  );
}
